import type { JSX } from 'react';
import { Button, Empty, Popconfirm, Space, Spin, Timeline, Typography } from 'antd';
import dayjs from 'dayjs';

import type { AppointmentHistoryEntry } from '@erp/appointments/api/appointments.api';
import RichTextDisplay from '@tools/components/RichTextDisplay.tsx';

import { buildHistoryStaffLabel } from './historyHelpers';
import type { StaffDirectoryEntry } from '@erp/services/api/serviceStaff';

const { Text } = Typography;

interface HistoryEntriesTimelineProps {
  entries: AppointmentHistoryEntry[];
  loading: boolean;
  staffDirectory: StaffDirectoryEntry[];
  canEdit: boolean;
  canDelete: boolean;
  deleteLoading: boolean;
  pendingDeleteId: number | null;
  onEdit: (entry: AppointmentHistoryEntry) => void;
  onDelete: (entry: AppointmentHistoryEntry) => void;
}

export function HistoryEntriesTimeline({
  entries,
  loading,
  staffDirectory,
  canEdit,
  canDelete,
  deleteLoading,
  pendingDeleteId,
  onEdit,
  onDelete,
}: HistoryEntriesTimelineProps): JSX.Element {
  if (loading) {
    return <Spin />;
  }

  if (entries.length === 0) {
    return <Empty description="Sin registros todavía" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <Timeline
      items={entries.map((entry) => ({
        key: entry.id,
        color: 'gray',
        children: (
          <Space orientation="vertical" style={{ width: '100%' }} size={4}>
            <Space wrap>
              <Text strong>{buildHistoryStaffLabel(entry.staff_user_id, staffDirectory, entry)}</Text>
              <Text type="secondary">{dayjs(entry.created_at).format('DD/MM/YYYY HH:mm')}</Text>
            </Space>

            <RichTextDisplay value={entry.notes} emptyText="Sin nota registrada" />

            <Space wrap size={8}>
              {canEdit ? (
                <Button
                  type="link"
                  size="small"
                  onClick={() => onEdit(entry)}
                  style={{ paddingLeft: 0 }}
                >
                  Editar
                </Button>
              ) : null}
              {canDelete ? (
                <Popconfirm
                  title="Eliminar registro"
                  description="Esta acción no se puede deshacer"
                  okText="Eliminar"
                  okButtonProps={{
                    danger: true,
                    loading: pendingDeleteId === entry.id && deleteLoading,
                  }}
                  cancelText="Cancelar"
                  disabled={deleteLoading}
                  onConfirm={() => onDelete(entry)}
                >
                  <Button
                    type="link"
                    size="small"
                    danger
                    loading={pendingDeleteId === entry.id && deleteLoading}
                    style={{ paddingLeft: 0 }}
                  >
                    Eliminar
                  </Button>
                </Popconfirm>
              ) : null}
            </Space>
          </Space>
        ),
      }))}
    />
  );
}

export default HistoryEntriesTimeline;
